import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, ExternalLink, Loader2 } from "lucide-react";
import { useSbAuth } from "@/context/SupabaseAuthContext";
import { supabase } from "@/lib/supabaseClient";
import { assistantContext, list } from "@/lib/dataStore";

const SUGGESTIONS = [
  "Hur gör jag en pizzasallad?",
  "Vad gör jag om en kund klagar på väntetiden?",
  "Hur många gram ost på Vesuvio?",
];

export function AssistantWidget() {
  const { currentRestaurant } = useSbAuth();
  const rid = currentRestaurant?.id;
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [messages, setMessages] = useState([]);
  const scrollRef = useRef(null);

  useEffect(() => {
    const onOpen = () => setOpen(true);
    window.addEventListener("open-assistant", onOpen);
    return () => window.removeEventListener("open-assistant", onOpen);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  if (!rid) return null;

  const openSource = (m) => {
    if (m.source_type === "manual" && m.source_id) {
      const doc = list(rid, "manuals").find((x) => x.id === m.source_id);
      const dept = doc?.department || "kunskap";
      setOpen(false);
      nav(`/konto/operation/${dept}`, { state: { tab: "manualer", openId: m.source_id, openType: "manual" } });
    }
  };

  const ask = async (text) => {
    const question = (text ?? q).trim();
    if (!question || busy) return;
    setQ("");
    setMessages((m) => [...m, { role: "user", text: question }]);
    setBusy(true);
    try {
      const { data: sess } = await supabase.auth.getSession();
      const token = sess?.session?.access_token;
      if (!token) throw new Error("Din session har gått ut – logga in igen");
      const ctx = assistantContext(rid);
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/assistant`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ question, ...ctx }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Assistenten kunde inte svara");
      const hasManualSource = data.source_type === "manual" && data.source_id && list(rid, "manuals").some((x) => x.id === data.source_id);
      setMessages((m) => [...m, { role: "ai", text: data.answer || "Jag hittade inget svar.", source_type: hasManualSource ? "manual" : "none", source_id: data.source_id }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "ai", text: e.message || "Något gick fel. Försök igen." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* Launcher */}
      <button onClick={() => setOpen((o) => !o)} data-testid="assistant-toggle" aria-label="Assistenten"
        className="fixed bottom-24 left-6 md:bottom-6 z-40 flex items-center gap-2 h-14 pl-4 pr-5 rounded-full bg-foreground text-background shadow-[0_10px_30px_-6px_rgba(20,20,20,0.4)] hover:scale-105 active:scale-95 transition-transform">
        <Sparkles className="h-5 w-5" />
        <span className="text-sm font-semibold">Assistenten</span>
      </button>

      <AnimatePresence>
        {open && (
          <>
            <motion.div className="fixed inset-0 z-40 bg-foreground/10 md:bg-transparent md:pointer-events-none"
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)} data-testid="assistant-backdrop" />
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20, scale: 0.98 }}
              transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="fixed z-50 bg-card border border-border shadow-2xl flex flex-col overflow-hidden
                inset-x-3 bottom-3 top-20 rounded-2xl
                md:inset-auto md:left-6 md:bottom-6 md:top-auto md:w-[400px] md:h-[560px]"
              data-testid="assistant-panel">
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3.5 border-b border-border bg-secondary/40">
                <div className="flex items-center gap-2.5">
                  <span className="h-9 w-9 grid place-items-center rounded-xl bg-foreground text-background"><Sparkles className="h-4.5 w-4.5" /></span>
                  <div>
                    <p className="font-display font-bold leading-none">Assistenten</p>
                    <p className="text-xs text-success flex items-center gap-1 mt-1"><span className="h-1.5 w-1.5 rounded-full bg-success inline-block" />Online</p>
                  </div>
                </div>
                <button onClick={() => setOpen(false)} data-testid="assistant-close" className="grid place-items-center h-8 w-8 rounded-lg text-muted-foreground hover:bg-secondary transition-colors"><X className="h-4 w-4" /></button>
              </div>

              {/* Messages */}
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="assistant-messages">
                {messages.length === 0 && (
                  <div className="text-center py-6">
                    <p className="text-sm text-muted-foreground mb-4">Fråga vad som helst om rutiner, recept och policys.</p>
                    <div className="space-y-2">
                      {SUGGESTIONS.map((s, i) => (
                        <button key={i} onClick={() => ask(s)} data-testid={`assistant-suggestion-${i}`}
                          className="w-full text-left text-sm px-3.5 py-2.5 rounded-xl border border-border bg-secondary/30 hover:bg-secondary/70 transition-colors">{s}</button>
                      ))}
                    </div>
                  </div>
                )}
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-foreground text-background rounded-br-sm" : "bg-secondary text-foreground rounded-bl-sm"}`}>
                      {m.text}
                      {m.source_type === "manual" && (
                        <button onClick={() => openSource(m)} data-testid={`assistant-source-${i}`}
                          className="mt-2.5 flex items-center gap-1.5 text-xs font-semibold text-primary hover:underline">
                          <ExternalLink className="h-3.5 w-3.5" />Visa instruktion
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {busy && (
                  <div className="flex justify-start"><div className="bg-secondary rounded-2xl rounded-bl-sm px-3.5 py-2.5"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div></div>
                )}
              </div>

              {/* Input */}
              <div className="p-3 border-t border-border">
                <div className="flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-1.5 focus-within:border-[hsl(var(--border-hover))] transition-colors">
                  <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") ask(); }}
                    placeholder="Ställ en fråga…" data-testid="assistant-input"
                    className="flex-1 bg-transparent text-sm outline-none py-1.5" />
                  <button onClick={() => ask()} disabled={busy || !q.trim()} data-testid="assistant-send"
                    className="grid place-items-center h-9 w-9 rounded-lg bg-primary text-primary-foreground disabled:opacity-40 hover:opacity-90 transition-opacity shrink-0">
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}
